import ReactApexChart from "react-apexcharts";

// Barras dos participantes que mais acessaram o evento: uma coluna por
// participante, com a quantidade de acessos no eixo Y.
//
// Nasceu de uma cópia de GameRd/Chart.js e não de um import dele: lá as
// categorias são `description` da configuração e o eixo Y conta participantes,
// aqui é o contrário — nome do participante na coluna e contagem no eixo. O
// gráfico de /event/gameRD continua sendo aquele, intocado.
//
// `expanded` é o mesmo gráfico dentro do modal: muda só a altura, para o
// ampliado não virar outra leitura.

// A partir daqui o nome é cortado no rótulo do eixo. O nome inteiro continua no
// tooltip, que lê a categoria original e não passa por este formatter.
const MAX_LABEL = 18;

// Largura reservada por coluna. Maior que os 60px do gráfico do GameRd porque
// aqui o rótulo é um nome de pessoa, não um código curto; abaixo disso os
// nomes se sobrepõem mesmo inclinados.
const BAR_WIDTH = 90;

// Altura do gráfico no card e no teto do ampliado.
const CARD_HEIGHT = 380;
const EXPANDED_HEIGHT = 520;

// Espaço que o modal gasta em volta do gráfico: margem do .modal, header,
// padding do .modal-body e uma folga. Serve para o ampliado caber na janela
// em vez de empurrar o modal para além da tela e virar scroll vertical.
const MODAL_CHROME = 200;

// Abaixo disto o gráfico fica ilegível e é melhor rolar mesmo
const MIN_HEIGHT = 300;

// Altura do ampliado limitada pela janela. Calculada no render e sem listener
// de resize de propósito: o Modal do Reactstrap só monta os filhos quando
// abre, então a conta acontece na abertura, com a janela no tamanho atual.
function expandedHeight() {
  if (typeof window === "undefined") return EXPANDED_HEIGHT;

  const available = window.innerHeight - MODAL_CHROME;
  return Math.max(MIN_HEIGHT, Math.min(EXPANDED_HEIGHT, available));
}

// Acessos são inteiros: sem isto o Apex rotula 0,5 quando o maior valor é
// baixo. Devolve o valor original quando não é número — em alguns estados o
// Apex chama o formatter sem dado.
function roundLabel(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : value;
}

export default function AccessChart(props) {
  const { chartData, expanded = false } = props;

  const rows = Array.isArray(chartData) ? chartData : [];

  const series = [
    {
      name: "Acessos",
      data: rows.map((row) => Number(row.total)),
    },
  ];

  const options = {
    chart: {
      toolbar: {
        show: false,
      },
    },
    colors: ["#04724c"],
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "40%",
        endingShape: "rounded",
      },
    },
    dataLabels: {
      // no ampliado o número cabe dentro da barra e evita ter de conferir o
      // eixo para ler o valor
      enabled: expanded,
      style: {
        // branco: o rótulo fica sobre o verde da barra, onde o preto some.
        // Sem offsetY junto com isso — deslocado para fora da barra, o branco
        // cairia no fundo claro do card
        colors: ["#fff"],
      },
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    xaxis: {
      categories: rows.map((row) => row.name ?? ""),
      labels: {
        // inclinados e sempre: com 30 nomes na horizontal o Apex esconde parte
        // dos rótulos, e uma coluna sem nome não diz nada
        rotate: -45,
        rotateAlways: true,
        hideOverlappingLabels: false,
        trim: false,
        maxHeight: 120,
        style: {
          fontSize: "11px",
        },
        formatter: (value) =>
          typeof value === "string" && value.length > MAX_LABEL
            ? `${value.slice(0, MAX_LABEL)}…`
            : value,
      },
    },
    yaxis: {
      title: {
        text: "Quantidade de acessos",
      },
      labels: {
        formatter: roundLabel,
      },
    },
    grid: {
      borderColor: "#f1f1f1",
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: function (val) {
          return val;
        },
      },
    },
  };

  // largura dinâmica: 30 colunas não cabem nem no card nem no modal, então a
  // div interna cresce e o scroll fica no eixo X, como no gráfico do GameRd.
  // No modal a área visível é o dobro, então entram ~12 colunas por vez
  const chartWidth = rows.length * BAR_WIDTH;

  return (
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: chartWidth }}>
        <ReactApexChart
          options={options}
          series={series}
          type="bar"
          height={expanded ? expandedHeight() : CARD_HEIGHT}
        />
      </div>
    </div>
  );
}
